/**
 * Остальные разделы API: проекты, лента, файлы, представления, уведомления,
 * поиск, метамодель и realtime.
 *
 * Один файл вместо семи: каждый из них — десяток строк, и разносить их по
 * файлам значит заставлять читателя открывать семь вкладок, чтобы понять,
 * как устроен один экран.
 */

import { newIdempotencyKey, request } from "@/shared/api/client";
import type { Members, Task } from "@/shared/api/tasks";

// ── Метамодель ───────────────────────────────────────────────────────────────

export interface Status {
  id: number;
  code: string;
  name: string;
  kind: string;
  color: string;
  position: number;
  is_final: boolean;
}

/**
 * Статусы: без типа задачи — все статусы контура.
 *
 * Список, планировщик и отбор показывают типы вперемешку, поэтому им нужен
 * весь набор. Карточке нужен только свой тип: чужие статусы в переключателе
 * предлагали бы переход, которого нет.
 */
export function listStatuses(objectId?: number): Promise<Status[]> {
  const suffix = objectId === undefined ? "" : `?object_id=${String(objectId)}`;
  return request<Status[]>(`/api/statuses${suffix}`);
}

// ── Проекты ──────────────────────────────────────────────────────────────────

export interface Project {
  id: number;
  parent_id: number | null;
  group_id: number | null;
  name: string;
  path: string;
  owner_id: number | null;
  is_archived: boolean;
  is_hidden: boolean;
  /** Проект в корзине: приходит, только если о нём спросили явно. */
  is_deleted: boolean;
  /** Наше юрлицо, от имени которого ведётся проект. */
  company_id: number | null;
  /** Заказчик. Та же запись справочника, что и в счетах, — чтобы деньги сходились. */
  counterparty_id: number | null;
  depth: number;
}

export function listProjects(includeArchived = false, includeDeleted = false): Promise<Project[]> {
  const query = new URLSearchParams({
    include_archived: String(includeArchived),
    include_deleted: String(includeDeleted),
  });
  return request<Project[]>(`/api/projects?${query.toString()}`);
}

export function renameProject(projectId: number, name: string): Promise<Project> {
  return request<Project>(`/api/projects/${String(projectId)}`, {
    method: "PATCH",
    body: { name },
  });
}

/** Завершить проект или вернуть его в работу. Завершённый уходит из дерева. */
export function archiveProject(projectId: number, isArchived: boolean): Promise<Project> {
  return request<Project>(`/api/projects/${String(projectId)}/archive`, {
    method: "POST",
    body: { archived: isArchived },
  });
}

/** Убрать проект в корзину — вместе со вложенными. Задачи остаются. */
export function deleteProject(projectId: number): Promise<undefined> {
  return request<undefined>(`/api/projects/${String(projectId)}`, { method: "DELETE" });
}

export function restoreProject(projectId: number): Promise<Project> {
  return request<Project>(`/api/projects/${String(projectId)}/restore`, { method: "POST" });
}

export function createProject(draft: {
  name: string;
  parent_id?: number | null;
  is_hidden?: boolean;
}): Promise<Project> {
  return request<Project>("/api/projects", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function getProject(projectId: number): Promise<Project> {
  return request<Project>(`/api/projects/${String(projectId)}`);
}

/**
 * Правка карточки проекта.
 *
 * Пустое поле — это «не трогать»: чтобы убрать заказчика или ответственного,
 * его имя перечисляют в `clear`. Иначе одно от другого неотличимо.
 */
export interface ProjectPatch {
  name?: string;
  owner_id?: number;
  group_id?: number;
  company_id?: number;
  counterparty_id?: number;
  is_hidden?: boolean;
  clear?: string[];
}

export function updateProject(projectId: number, patch: ProjectPatch): Promise<Project> {
  return request<Project>(`/api/projects/${String(projectId)}`, { method: "PATCH", body: patch });
}

export type ProjectRole = "manager" | "member" | "observer";

export const PROJECT_ROLE_NAMES: Record<ProjectRole, string> = {
  manager: "Руководитель",
  member: "Участник",
  observer: "Наблюдатель",
};

export interface ProjectMember {
  /** Совпадает с идентификатором пользователя: у человека principal — он сам. */
  principal_id: number;
  role: ProjectRole;
  project_id: number;
  /** Роль досталась от проекта выше: снимать её надо там же, где выдали. */
  inherited: boolean;
}

export function projectMembers(projectId: number): Promise<ProjectMember[]> {
  return request<ProjectMember[]>(`/api/projects/${String(projectId)}/members`);
}

export function addProjectMember(
  projectId: number,
  principalId: number,
  role: ProjectRole,
): Promise<undefined> {
  return request<undefined>(`/api/projects/${String(projectId)}/members`, {
    method: "POST",
    body: { principal_id: principalId, role },
  });
}

export function removeProjectMember(
  projectId: number,
  principalId: number,
  role?: ProjectRole,
): Promise<undefined> {
  const suffix = role === undefined ? "" : `?role=${role}`;
  return request<undefined>(
    `/api/projects/${String(projectId)}/members/${String(principalId)}${suffix}`,
    { method: "DELETE" },
  );
}

export interface ProjectContact {
  id: number;
  contact_id: number;
  note: string | null;
}

export function projectContacts(projectId: number): Promise<ProjectContact[]> {
  return request<ProjectContact[]>(`/api/projects/${String(projectId)}/contacts`);
}

export function addProjectContact(
  projectId: number,
  contactId: number,
  note?: string,
): Promise<ProjectContact> {
  return request<ProjectContact>(`/api/projects/${String(projectId)}/contacts`, {
    method: "POST",
    body: { contact_id: contactId, note: note ?? null },
  });
}

export function removeProjectContact(projectId: number, contactId: number): Promise<undefined> {
  return request<undefined>(`/api/projects/${String(projectId)}/contacts/${String(contactId)}`, {
    method: "DELETE",
  });
}

export interface ProjectNote {
  id: number;
  project_id: number;
  author_id: number | null;
  body_text: string;
  created_at: string;
  updated_at: string;
}

export function projectNotes(projectId: number, limit = 50): Promise<ProjectNote[]> {
  return request<ProjectNote[]>(`/api/projects/${String(projectId)}/notes?limit=${String(limit)}`);
}

export function addProjectNote(projectId: number, bodyText: string): Promise<ProjectNote> {
  return request<ProjectNote>(`/api/projects/${String(projectId)}/notes`, {
    method: "POST",
    body: { body_text: bodyText },
    idempotencyKey: newIdempotencyKey(),
  });
}

export function deleteProjectNote(noteId: number): Promise<undefined> {
  return request<undefined>(`/api/projects/notes/${String(noteId)}`, { method: "DELETE" });
}

export interface StatusCount {
  status_id: number;
  name: string;
  kind: string;
  color: string;
  count: number;
}

export interface ProjectTaskSummary {
  total: number;
  done: number;
  overdue: number;
  by_status: StatusCount[];
}

/** Сводка живёт в модуле задач: считает тот, кто задачами владеет. */
export function projectTaskSummary(projectId: number): Promise<ProjectTaskSummary> {
  return request<ProjectTaskSummary>(`/api/projects/${String(projectId)}/task-summary`);
}

export interface ProjectExpense extends Expense {
  task_number: number;
  task_name: string;
}

export interface ProjectExpenseList {
  items: ProjectExpense[];
  totals: Record<string, string>;
}

export function projectExpenses(projectId: number): Promise<ProjectExpenseList> {
  return request<ProjectExpenseList>(`/api/projects/${String(projectId)}/expenses`);
}

export interface ProjectEvent extends FeedItem {
  task_number: number;
  task_name: string;
}

export interface ProjectFeedPage {
  items: ProjectEvent[];
  next_cursor: string | null;
  has_more: boolean;
}

export function projectFeed(projectId: number, limit = 30): Promise<ProjectFeedPage> {
  return request<ProjectFeedPage>(`/api/projects/${String(projectId)}/feed?limit=${String(limit)}`);
}

// ── Лента ────────────────────────────────────────────────────────────────────

export type Visibility = "all" | "staff" | "private";

export interface Attachment {
  id: number;
  name: string;
  mime: string;
  size: number;
  sha256: string | null;
  version: number;
  version_of_id: number | null;
  uploaded_by_id: number | null;
  created_at: string;
  is_inline: boolean;
}

export interface FeedItem {
  id: number;
  task_id: number;
  author_id: number | null;
  parent_id: number | null;
  action_id: number | null;
  kind: "human" | "system";
  system_kind: string | null;
  system_payload: Record<string, unknown>;
  visibility: Visibility;
  body: Record<string, unknown>;
  body_text: string;
  publish_at: string | null;
  is_published: boolean;
  edited_at: string | null;
  edit_count: number;
  moved_from_task_id: number | null;
  created_at: string;
  recipients: number[];
  attachments: Attachment[];
}

export interface FeedPage {
  items: FeedItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface FeedOperations {
  status_id?: number;
  add_members?: Partial<Members>;
  remove_members?: Partial<Members>;
  start_at?: string | null;
  end_at?: string | null;
  reminder_at?: string;
  reminder_note?: string;
  attachments?: number[];
}

export function feed(
  taskId: number,
  options: { beforeId?: number; afterId?: number; aroundId?: number; limit?: number } = {},
): Promise<FeedPage> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 50) });
  if (options.beforeId) {
    params.set("before_id", String(options.beforeId));
  }
  if (options.afterId) {
    params.set("after_id", String(options.afterId));
  }
  if (options.aroundId) {
    params.set("around_id", String(options.aroundId));
  }
  return request<FeedPage>(`/api/tasks/${taskId}/comments?${params.toString()}`);
}

export interface CommentDraft {
  body?: Record<string, unknown>;
  body_text: string;
  visibility?: Visibility;
  recipients?: number[];
  reply_to_id?: number;
  publish_at?: string;
  operations?: FeedOperations;
}

export function addComment(taskId: number, draft: CommentDraft): Promise<FeedItem> {
  return request<FeedItem>(`/api/tasks/${taskId}/comments`, {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function editComment(
  commentId: number,
  body: Record<string, unknown>,
  bodyText: string,
): Promise<FeedItem> {
  return request<FeedItem>(`/api/comments/${commentId}`, {
    method: "PATCH",
    body: { body, body_text: bodyText },
  });
}

export function deleteComment(commentId: number): Promise<void> {
  return request<undefined>(`/api/comments/${commentId}`, { method: "DELETE" });
}

export function moveComment(commentId: number, taskId: number): Promise<FeedItem> {
  return request<FeedItem>(`/api/comments/${commentId}/move`, {
    method: "POST",
    body: { task_id: taskId },
  });
}

export function commentToTask(
  commentId: number,
  name: string,
): Promise<{ task_id: number; number: number }> {
  return request<{ task_id: number; number: number }>(`/api/comments/${commentId}/to-task`, {
    method: "POST",
    body: { name },
  });
}

// ── Файлы ────────────────────────────────────────────────────────────────────

export async function uploadFile(file: File, target?: { target: "task"; target_id: number }) {
  const ticket = await request<{ key: string; url: string }>("/api/files/upload-url", {
    method: "POST",
    body: { name: file.name, size: file.size },
  });

  // Файл уходит прямо в хранилище: через приложение он бы занял воркер
  // на всё время передачи
  const upload = await fetch(ticket.url, { method: "PUT", body: file });
  if (!upload.ok) {
    throw new Error("Не удалось загрузить файл");
  }

  return request<Attachment>("/api/files", {
    method: "POST",
    body: { key: ticket.key, name: file.name, ...(target ?? {}) },
  });
}

export function downloadUrl(fileId: number): Promise<{ url: string }> {
  return request<{ url: string }>(`/api/files/${fileId}/download-url`);
}

export function listFiles(target: "task" | "project", targetId: number): Promise<Attachment[]> {
  return request<Attachment[]>(`/api/files?target=${target}&target_id=${targetId}`);
}

export function deleteFile(fileId: number): Promise<void> {
  return request<undefined>(`/api/files/${String(fileId)}`, { method: "DELETE" });
}

// Представления живут в `shared/api/views.ts`: они неотделимы от отбора

// ── Уведомления ──────────────────────────────────────────────────────────────

export interface Notification {
  id: number;
  type: string;
  task_id: number | null;
  comment_id: number | null;
  actor_id: number | null;
  payload: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

export function inbox(unreadOnly = false): Promise<{ items: Notification[]; unread: number }> {
  return request<{ items: Notification[]; unread: number }>(
    `/api/notifications?unread_only=${unreadOnly}`,
  );
}

export function markNotificationsRead(ids?: number[]): Promise<{ marked: number }> {
  return request<{ marked: number }>("/api/notifications/read", {
    method: "POST",
    body: { ids: ids ?? null },
  });
}

// ── Поиск ────────────────────────────────────────────────────────────────────

export interface SearchResponse {
  tasks: {
    id: number;
    number: number;
    name: string;
    status_id: number;
    project_id: number | null;
  }[];
  comments: {
    comment_id: number;
    task_id: number;
    author_id: number | null;
    created_at: string;
    headline: string;
  }[];
  files: { id: number; name: string; mime: string; size: number; created_at: string }[];
  total: number;
}

export function search(query: string, limit = 10): Promise<SearchResponse> {
  return request<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

// ── Realtime ─────────────────────────────────────────────────────────────────

export function connectionToken(): Promise<{ token: string; url: string }> {
  return request<{ token: string; url: string }>("/api/realtime/token", { method: "POST" });
}

export function subscriptionToken(channel: string): Promise<{ channel: string; token: string }> {
  return request<{ channel: string; token: string }>("/api/realtime/subscription", {
    method: "POST",
    body: { channel },
  });
}

// ── Справочники ──────────────────────────────────────────────────────────────

export interface Company {
  id: number;
  name: string;
  legal_name: string | null;
  inn: string | null;
  kpp: string | null;
  is_active: boolean;
  position: number;
}

export interface Counterparty {
  id: number;
  name: string;
  inn: string | null;
  kpp: string | null;
  is_auto: boolean;
  is_active: boolean;
}

export function listCompanies(includeInactive = false): Promise<Company[]> {
  return request<Company[]>(`/api/companies?include_inactive=${includeInactive}`);
}

export function createCompany(draft: {
  name: string;
  legal_name?: string;
  inn?: string;
  kpp?: string;
}): Promise<Company> {
  return request<Company>("/api/companies", { method: "POST", body: draft });
}

export function updateCompany(
  companyId: number,
  patch: Partial<Pick<Company, "name" | "legal_name" | "inn" | "kpp" | "is_active" | "position">>,
): Promise<Company> {
  return request<Company>(`/api/companies/${companyId}`, { method: "PATCH", body: patch });
}

export function deleteCompany(companyId: number): Promise<undefined> {
  return request<undefined>(`/api/companies/${companyId}`, { method: "DELETE" });
}

export function listCounterparties(
  query?: string,
  includeInactive = false,
): Promise<Counterparty[]> {
  const params = new URLSearchParams();
  if (query) {
    params.set("q", query);
  }
  if (includeInactive) {
    params.set("include_inactive", "true");
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return request<Counterparty[]>(`/api/counterparties${suffix}`);
}

export function getCounterparty(counterpartyId: number): Promise<Counterparty> {
  return request<Counterparty>(`/api/counterparties/${counterpartyId}`);
}

/** Правка реквизитов и отключение. Удаления нет: на контрагента ссылаются задачи. */
export function updateCounterparty(
  counterpartyId: number,
  patch: Partial<Pick<Counterparty, "name" | "inn" | "kpp" | "is_active">>,
): Promise<Counterparty> {
  return request<Counterparty>(`/api/counterparties/${counterpartyId}`, {
    method: "PATCH",
    body: patch,
  });
}

export function createCounterparty(draft: {
  name: string;
  inn?: string;
  kpp?: string;
}): Promise<Counterparty> {
  return request<Counterparty>("/api/counterparties", { method: "POST", body: draft });
}

// ── Расходы ──────────────────────────────────────────────────────────────────

export interface Expense {
  id: number;
  task_id: number;
  entry_date: string;
  amount: string;
  currency: string;
  invoice_number: string | null;
  counterparty_id: number | null;
  counterparty_name: string | null;
  file_id: number | null;
  note: string | null;
  author_id: number;
  created_at: string;
}

export interface ExpenseList {
  items: Expense[];
  totals: Record<string, string>;
}

export function listExpenses(taskId: number): Promise<ExpenseList> {
  return request<ExpenseList>(`/api/tasks/${taskId}/expenses`);
}

export function addExpense(
  taskId: number,
  draft: {
    amount: string;
    currency?: string;
    entry_date?: string;
    invoice_number?: string;
    counterparty_id?: number;
    file_id?: number;
    note?: string;
  },
): Promise<Expense> {
  return request<Expense>(`/api/tasks/${taskId}/expenses`, {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface ExpensePatch {
  amount?: string;
  currency?: string;
  entry_date?: string;
  invoice_number?: string;
  counterparty_id?: number;
  note?: string;
  /** Что очистить: иначе «убрать номер счёта» не отличить от «не трогать». */
  clear?: string[];
}

export function updateExpense(
  taskId: number,
  entryDate: string,
  entryId: number,
  patch: ExpensePatch,
): Promise<Expense> {
  return request<Expense>(`/api/tasks/${String(taskId)}/expenses/${entryDate}/${String(entryId)}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteExpense(
  taskId: number,
  entryDate: string,
  entryId: number,
): Promise<undefined> {
  return request<undefined>(`/api/tasks/${taskId}/expenses/${entryDate}/${entryId}`, {
    method: "DELETE",
  });
}

/** Распознанный счёт: пустые поля означают «не удалось прочитать», а не ноль. */
export interface InvoiceReading {
  file_id: number;
  recognized: boolean;
  amount: string | null;
  currency: string;
  number: string | null;
  issued_on: string | null;
  supplier_name: string | null;
  supplier_inn: string | null;
  counterparty_id: number | null;
}

export function readInvoice(fileId: number): Promise<InvoiceReading> {
  return request<InvoiceReading>(`/api/files/${fileId}/invoice`, { method: "POST" });
}

// ── Люди ─────────────────────────────────────────────────────────────────────

export interface Colleague {
  id: number;
  email: string;
  full_name: string;
  system_role: string;
  is_active: boolean;
}

export function listPeople(): Promise<Colleague[]> {
  return request<Colleague[]>("/api/users");
}

export function me(): Promise<Colleague> {
  return request<Colleague>("/api/users/me");
}

export interface NewColleague {
  email: string;
  full_name: string;
  short_name?: string;
  system_role: string;
  password: string;
}

export function addColleague(payload: NewColleague): Promise<Colleague> {
  return request<Colleague>("/api/users", { method: "POST", body: payload });
}

export function updateColleague(
  userId: number,
  patch: Partial<Pick<Colleague, "full_name" | "system_role" | "is_active">>,
): Promise<Colleague> {
  return request<Colleague>(`/api/users/${String(userId)}`, { method: "PATCH", body: patch });
}

export function resetColleaguePassword(userId: number, password: string): Promise<void> {
  return request<undefined>(`/api/users/${String(userId)}/password`, {
    method: "POST",
    body: { password },
  });
}

export type { Task };
